/** W3 widget enter/exit + smooth on-air edits (PMA-BCAST-014). CSS transform/opacity only. */
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

export type AnimType = "none" | "fade" | "slide" | "scale";
export interface AnimSpec {
  type: AnimType;
  from?: "top" | "bottom" | "left" | "right";
  duration_ms?: number;
}

export const SMOOTH_EDIT_MS = 300;
export const MAX_WIDGET_ANIM_MS = 1500;

export const clampAnimMs = (ms: unknown): number =>
  typeof ms === "number" && Number.isFinite(ms)
    ? Math.min(MAX_WIDGET_ANIM_MS, Math.max(0, Math.round(ms)))
    : 0;

const SLIDE: Record<string, string> = {
  top: "translateY(-100%)",
  bottom: "translateY(100%)",
  left: "translateX(-100%)",
  right: "translateX(100%)",
};

/** The hidden-state style (start of enter, end of exit). */
export function hiddenStyle(spec: AnimSpec | undefined): CSSProperties {
  if (!spec || spec.type === "none") return {};
  if (spec.type === "fade") return { opacity: 0 };
  if (spec.type === "scale") return { opacity: 0, transform: "scale(0.85)" };
  return { transform: SLIDE[spec.from ?? "bottom"], opacity: 0 };
}

const SHOWN: CSSProperties = { opacity: 1, transform: "none" };

export const transitionCss = (ms: number) =>
  `transform ${ms}ms ease, opacity ${ms}ms ease`;

/** Geometry, colour and opacity glide for on-air edits. */
export function smoothEditStyle(enabled: boolean): CSSProperties {
  if (!enabled) return {};
  const t = SMOOTH_EDIT_MS;
  return {
    transition: `left ${t}ms ease, top ${t}ms ease, width ${t}ms ease, height ${t}ms ease, background-color ${t}ms ease, border-color ${t}ms ease, color ${t}ms ease, opacity ${t}ms ease`,
  };
}

/**
 * Mounts `children` while visible; plays `enter` on show and `exit` on hide,
 * keeping the node mounted until the exit finishes.
 */
export function EnterExit({
  visible,
  enter,
  exit,
  children,
}: {
  visible: boolean;
  enter?: AnimSpec;
  exit?: AnimSpec;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(visible);
  const [shown, setShown] = useState(
    visible &&
      !(enter && enter.type !== "none" && clampAnimMs(enter.duration_ms) > 0),
  );
  const enterMs =
    enter && enter.type !== "none" ? clampAnimMs(enter.duration_ms) : 0;
  const exitMs =
    exit && exit.type !== "none" ? clampAnimMs(exit.duration_ms) : 0;

  useEffect(() => {
    if (visible) {
      setMounted(true);
      if (enterMs === 0) {
        setShown(true);
        return;
      }
      const id = setTimeout(() => setShown(true), 0);
      return () => clearTimeout(id);
    }
    setShown(false);
    if (exitMs === 0) {
      setMounted(false);
      return;
    }
    const id = setTimeout(() => setMounted(false), exitMs);
    return () => clearTimeout(id);
  }, [visible, enterMs, exitMs]);

  if (!mounted) return null;
  const spec = visible ? enter : exit;
  const ms = visible ? enterMs : exitMs;
  const style: CSSProperties =
    ms === 0
      ? {}
      : shown
        ? { ...SHOWN, transition: transitionCss(ms) }
        : { ...hiddenStyle(spec), transition: transitionCss(ms) };
  return (
    <div
      data-testid="broadcast-enter-exit"
      data-anim-state={shown ? "shown" : "hidden"}
      style={{ width: "100%", height: "100%", ...style }}
    >
      {children}
    </div>
  );
}
